import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import "./InterestLoanCalculator.css";
import { useForm, FormProvider } from "react-hook-form";
import Inputmask from "inputmask";
import { commonService, toasterService } from "../../services";
import { FormField } from "../../components/FormField/FormField";
import { FormSections } from "../../components/FormSections/FormSections";
import { EligibilityTip } from "../../components/EligibilityTip/EligibilityTip";
import { EncompassPrefill } from "../../components/EncompassPrefill/EncompassPrefill";
import { RunHistoryLayout, useRunHistory, buildRunLabel } from "../../components/RunHistory/RunHistory";
//import { LoanCalculatorOutput } from "../LoanCalculatorOutput/LoanCalculatorOutput";
import { OutputReports } from "../OutputReports/OutputReports";
import { useAuthState } from "../../contexts/context";
import { Role } from "../../helpers";
import licensedStates from "../../constant/licensedState";

export function InterestLoanCalculator() {
  const [elements, setElements] = useState(null);
  const [selectedOption, setSelectedOption] = useState(true);
  const [loading, showLoader] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);
  const [outputData, setOutputData] = useState(null);
  // Lock date from a pulled Encompass loan — drives the external user's
  // (read-only) pricing-date display. The server enforces the actual date
  // from the signed token regardless of anything shown here.
  const [pulledLock, setPulledLock] = useState(null);
  const runHistory = useRunHistory("bf-runs-calculator");

  const methods = useForm();
  const userDetails = useAuthState();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = methods;

  const fields = elements ?? [];

  const validaForm = (data) => {
    const st = data.property_state?.value;
    if (st && !licensedStates.includes(st) && userDetails.role === Role.External) {
      toasterService.error(
        "Not licensed in {state}. Coming Soon!".replace(
          "{state}",
          data.property_state?.label || st
        )
      );
      return false;
    }
    return true;
  };
  const onSubmit = (data) => {
    if (validaForm(data)) {
      commonService
        .submitInterestLoanForm(data)
        .then((res) => {
          setFormSuccess(true);
          getOutputReport(res.data.data, 3, data);
          window.scrollTo({ top: 0, behavior: "smooth" });
        })
        .catch((error) => {
          toasterService.error((error?.response?.data?.message||"Something went wrong. Please try again."));
        });
    }
  };

  useEffect(() => {
    getFormFields(true);
    setFormSuccess(false);
    setReportSuccess(false);
    commonService
      .getMyRuns()
      .then((resp) => runHistory.seedServerRuns(resp.data))
      .catch(() => {});
  }, []);

  // The fixed anchored submit bar covers the viewport bottom while this page is
  // mounted; flag <body> so the site footer gets bottom clearance (index.css).
  useEffect(() => {
    document.body.classList.add("bf-has-submitbar");
    return () => document.body.classList.remove("bf-has-submitbar");
  }, []);

  const handleSelectRun = (id) => {
    runHistory.selectRun(id);
    const run = runHistory.runs.find((r) => r.id === id);
    if (run && !run.outputData && run.serverId) {
      commonService
        .loadMyRun(run.serverId)
        .then((resp) => runHistory.setRunOutput(id, resp.data))
        .catch((e) =>
          toasterService.error(
            e?.response?.data?.message ||
              "Couldn't load that run. Please try again."
          )
        );
    }
  };

  function getOutputReport(filename, type, inputs) {
    commonService
      .generateReport({ file: filename, type: type })
      .then((resp) => {
        setOutputData(resp.data);
        setFormSuccess((formSuccess) => (formSuccess = false));
        setReportSuccess((reportSuccess) => (reportSuccess = true));
        runHistory.addRun({
          filename,
          type,
          isLC: true,
          product: "Quote",
          label: buildRunLabel(inputs, "Quote"),
          outputData: resp.data,
        });
      })
      .catch((error) => {
        toasterService.error((error?.response?.data?.message||"Something went wrong. Please try again."), {
          hideProgressBar: true,
          autoClose: false,
        });
        setFormSuccess((formSuccess) => (formSuccess = false));
      });
  }

  function getFormFields(value) {
    showLoader(true);
    // Internal users get the hidden=1 scenario fields (pricing date, price /
    // yield targets, HPI knobs) in the "Internal only" section. External users
    // get the shared set; the server enforces their defaults regardless.
    commonService
      .getFormFields("I", userDetails.role === Role.Internal ? "i" : "e")
      .then((data) => {
        setElements(data.data);
        setSelectedOption((selectedOption) => (selectedOption = true));
        Inputmask().mask(document.querySelectorAll("input"));
        showLoader((loading) => (loading = false));
      })
      .catch((error) => {
        toasterService.error((error?.response?.data?.message||"Something went wrong. Please try again."));
      });
  }

  return (
    <div>
      <div className="form-container">
        {formSuccess && (
          <div className="form-success">
            <h4 className="display-6 text-center">
              Processing. Please don't close this window. Can take up to 2
              minutes.
            </h4>
            <div className="progress mt-4">
              <div
                className="progress-bar-fb"
                style={{ background: "#337ab7" }}
              ></div>
            </div>
          </div>
        )}
        {!formSuccess && (
          <RunHistoryLayout
            runs={runHistory.runs}
            activeRunId={runHistory.activeRunId}
            onSelect={handleSelectRun}
            onNew={runHistory.newRun}
          >
            {runHistory.activeRun && (
              <div>
                <a
                  className="text-primary text-decoration-none"
                  href="javascript:;"
                  onClick={runHistory.newRun}
                >
                  <i className="bi bi-arrow-left-circle"></i> Go back / New run
                </a>

                {runHistory.activeRun.outputData ? (
                  <OutputReports outputData={runHistory.activeRun.outputData} isLC />
                ) : (
                  <div className="d-flex justify-content-center py-5">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading run…</span>
                    </div>
                  </div>
                )}
                {runHistory.activeRun.outputData && (
                  <div className="bf-submitbar">
                    <div className="bf-submitbar-inner">
                      <span className="bf-submitbar-text">
                        Indicate your preferred loan option, then submit it.
                      </span>
                      <a
                        className="btn btn-primary bf-btn-handoff bf-submitbar-btn"
                        href="https://buttonfinance.encompasstpoconnect.com/#/content/login"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Submit this loan →
                      </a>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div hidden={!!runHistory.activeRun}>
              <hr />
              <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  {!loading && fields.length > 0 && <EligibilityTip />}
                  {loading && (
                    <div key="loader" className="d-flex justify-content-center">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}
                  {!loading && fields.length > 0 && (
                    <FormSections
                      fields={fields}
                      midSlot={
                        <EncompassPrefill fields={fields} onPull={setPulledLock} />
                      }
                    />
                  )}

                  {!loading && fields.length > 0 &&
                    userDetails.role !== Role.Internal && (
                      <section className="bf-formsection">
                        <h6 className="bf-section-title">Pricing Date</h6>
                        <div className="mb-3">
                          <input
                            className={
                              "form-control" +
                              (pulledLock && pulledLock.lockDate
                                ? " bf-pricing-date-historical"
                                : "")
                            }
                            style={{ maxWidth: "260px" }}
                            value={
                              pulledLock && pulledLock.lockDate
                                ? pulledLock.lockDate + "  (loan lock date)"
                                : new Date().toLocaleDateString()
                            }
                            disabled
                            readOnly
                          />
                        </div>
                        <small className="bf-field-help">
                          {pulledLock && pulledLock.lockDate
                            ? "HISTORICAL: this quote will price on the lock date's rate sheet, and its run id will start with H."
                            : "Quotes price on today's rate sheet. Pulling a locked loan from Encompass prices it as of its lock date."}
                        </small>
                      </section>
                    )}

                  {!loading && fields.length > 0 &&
                    userDetails.role === Role.Internal && (
                      <section
                        className={
                          "bf-formsection" +
                          (methods.watch("exception_pricing") === "True"
                            ? " bf-beta-armed"
                            : "")
                        }
                      >
                        <h6 className="bf-section-title">BETA</h6>
                        <label className="form-label">Price Exceptions</label>
                        <div className="mb-3" style={{ maxWidth: "260px" }}>
                          {/* What an exception can price is defined by the
                              pricing engine's eligibility data, never here. */}
                          <select
                            className={
                              "form-select" +
                              (methods.watch("exception_pricing") === "True"
                                ? " bf-exc-on"
                                : "")
                            }
                            defaultValue="False"
                            {...methods.register("exception_pricing")}
                          >
                            <option value="False">Off</option>
                            <option value="True">On</option>
                          </select>
                        </div>
                        <small
                          className={
                            "bf-field-help" +
                            (methods.watch("exception_pricing") === "True"
                              ? " bf-exc-warn"
                              : "")
                          }
                        >
                          Internal only — use at your own risk. Exception runs
                          are marked with an X run id and an exception banner
                          on the output.
                        </small>
                      </section>
                    )}

                  {!loading && fields.length > 0 && (
                    <div className="bf-submitbar">
                      <div className="bf-submitbar-inner">
                        <span className="bf-submitbar-text">
                          Review the details, then run your quote.
                        </span>
                        <input
                          className="btn btn-primary bf-submitbar-btn"
                          type="submit"
                          value="Price Loan"
                        />
                      </div>
                    </div>
                  )}
                </form>
              </FormProvider>
            </div>
          </RunHistoryLayout>
        )}
      </div>
    </div>
  );
}
